import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { highlightsService, type HighlightDto } from '../api/highlightsService';
import type { Note, HighlightColor, NoteType } from '../components/reader/types';
import { useState, useEffect } from 'react';
import { isNetworkOffline } from '../utils/offlineDetection';

/**
 * Map frontend HighlightColor (hex codes) to backend HighlightColor enum (integers)
 */
function mapColorToBackendEnum(color: HighlightColor): number {
  const colorMap: Record<string, number> = {
    '#FFEB3B': 1, // Yellow
    '#4CAF50': 2, // Green
    '#2196F3': 3, // Blue
    '#E91E63': 4, // Pink
    '#9C27B0': 5, // Purple
    '#FF9800': 6, // Orange
    '#F44336': 7, // Red
    '#009688': 8, // Teal
    '#CDDC39': 9, // Lime
    '#FFC107': 10, // Amber
  };
  return colorMap[color as string] || 1; // Default to Yellow if not found
}

/**
 * Map backend HighlightColor enum (integers) to frontend HighlightColor (hex codes)
 */
function mapBackendEnumToColor(backendColor: number | string): HighlightColor {
  const enumMap: Record<number, HighlightColor> = {
    1: '#FFEB3B' as HighlightColor, // Yellow
    2: '#4CAF50' as HighlightColor, // Green
    3: '#2196F3' as HighlightColor, // Blue
    4: '#E91E63' as HighlightColor, // Pink
    5: '#9C27B0' as HighlightColor, // Purple
    6: '#FF9800' as HighlightColor, // Orange
    7: '#F44336' as HighlightColor, // Red
    8: '#009688' as HighlightColor, // Teal
    9: '#CDDC39' as HighlightColor, // Lime
    10: '#FFC107' as HighlightColor, // Amber
  };
  const colorNum = typeof backendColor === 'string' ? parseInt(backendColor, 10) : backendColor;
  return enumMap[colorNum] || ('#FFEB3B' as HighlightColor); // Default to Yellow
}

/**
 * Convert backend HighlightDto to frontend Note type
 */
function mapDtoToNote(dto: HighlightDto): Note {
  // Determine note type based on selectedText
  // If selectedText is "[PAGE NOTE]" or empty, it's a page-level note
  const isPageNote = dto.selectedText === '[PAGE NOTE]' || dto.selectedText === '';

  return {
    id: dto.id,
    bookId: dto.bookId,
    userId: dto.userId,
    location: dto.location,
    locationLabel: dto.locationLabel,
    noteType: isPageNote ? 'page' as NoteType : 'text' as NoteType,
    selectedText: isPageNote ? undefined : dto.selectedText,
    color: mapBackendEnumToColor(dto.color),
    noteText: dto.noteText,
    createdAt: new Date(dto.creationTime),
    updatedAt: new Date(dto.lastModificationTime || dto.creationTime),
  };
}

/**
 * Hook to manage highlights/notes for a specific book
 * Supports both online and offline modes
 */
export function useHighlights(bookId: string) {
  const queryClient = useQueryClient();
  const queryKey = ['highlights', bookId];
  const [isOffline, setIsOffline] = useState(isNetworkOffline());

  // Listen for online/offline events
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Fetch all highlights (with offline fallback)
  const { data: highlights = [], isLoading, error } = useQuery({
    queryKey,
    queryFn: async () => {
      // Try online first
      if (!isNetworkOffline()) {
        try {
          const dtos = await highlightsService.getMyHighlights(bookId);
          return dtos.map(mapDtoToNote);
        } catch (err) {
          console.warn('Failed to fetch highlights from API, falling back to offline:', err);
          // Fall through to offline mode
        }
      }

      // Offline mode: load from IndexedDB
      console.log('📴 Loading highlights from offline storage...');
      const { offlineContentService } = await import('../services/offline/OfflineContentService');
      const offlineHighlights = await offlineContentService.getOfflineHighlights(bookId);

      // Convert HighlightData to Note
      return offlineHighlights.map((h) => ({
        id: h.id,
        bookId: h.bookId,
        userId: '', // Not stored offline
        location: h.location,
        locationLabel: '', // Not stored in HighlightData
        noteType: 'text' as NoteType,
        selectedText: h.text,
        color: h.color as HighlightColor,
        noteText: h.note,
        createdAt: h.createdAt,
        updatedAt: h.createdAt,
      }));
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Create highlight mutation (for text highlights)
  const createHighlight = useMutation({
    mutationFn: async ({
      location,
      locationLabel,
      selectedText,
      color,
      noteText,
    }: {
      location: string | number;
      locationLabel: string;
      selectedText: string;
      color: HighlightColor;
      noteText?: string;
    }) => {
      const dto = await highlightsService.createMyHighlight({
        bookId,
        location: String(location),
        locationLabel,
        selectedText,
        color: mapColorToBackendEnum(color), // Convert frontend hex color to backend enum integer
        noteText,
      });
      return mapDtoToNote(dto);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Create page note mutation (for page-level notes without text selection)
  const createPageNote = useMutation({
    mutationFn: async ({
      location,
      locationLabel,
      color,
      noteText,
    }: {
      location: string | number;
      locationLabel: string;
      color: HighlightColor;
      noteText?: string;
    }) => {
      const dto = await highlightsService.createPageNote({
        bookId,
        location: String(location),
        locationLabel,
        color: mapColorToBackendEnum(color), // Convert frontend hex color to backend enum integer
        noteText,
      });
      return mapDtoToNote(dto);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Update highlight mutation
  const updateHighlight = useMutation({
    mutationFn: async ({
      id,
      color,
      noteText,
    }: {
      id: string;
      color: HighlightColor;
      noteText?: string;
    }) => {
      const dto = await highlightsService.updateMyHighlight(id, bookId, {
        color: mapColorToBackendEnum(color), // Convert frontend hex color to backend enum integer
        noteText,
      });
      return mapDtoToNote(dto);
    },
    onSuccess: (updatedNote) => {
      // Update the cache directly instead of invalidating to avoid highlights disappearing
      queryClient.setQueryData<Note[]>(queryKey, (oldNotes) => {
        if (!oldNotes) return [updatedNote];
        return oldNotes.map((note) =>
          note.id === updatedNote.id ? updatedNote : note
        );
      });
    },
  });

  // Delete highlight mutation
  const deleteHighlight = useMutation({
    mutationFn: async (id: string) => {
      await highlightsService.deleteMyHighlight(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Get highlights filtered by color
  const getHighlightsByColor = async (
    color: HighlightColor
  ): Promise<Note[]> => {
    const dtos = await highlightsService.getMyHighlightsByColor(
      bookId,
      mapColorToBackendEnum(color) // Convert frontend hex color to backend enum integer
    );
    return dtos.map(mapDtoToNote);
  };

  // Get highlight counts by color
  const getHighlightCounts = async (): Promise<Record<string, number>> => {
    return await highlightsService.getHighlightCountsByColor(bookId);
  };

  return {
    highlights,
    isLoading,
    error,
    isOffline, // NEW: Expose offline status to UI
    createHighlight: isOffline
      ? async () => {
          throw new Error('Cannot create highlights in offline mode');
        }
      : createHighlight.mutateAsync,
    createPageNote: isOffline
      ? async () => {
          throw new Error('Cannot create notes in offline mode');
        }
      : createPageNote.mutateAsync,
    updateHighlight: isOffline
      ? async () => {
          throw new Error('Cannot update highlights in offline mode');
        }
      : updateHighlight.mutateAsync,
    deleteHighlight: isOffline
      ? async () => {
          throw new Error('Cannot delete highlights in offline mode');
        }
      : deleteHighlight.mutateAsync,
    getHighlightsByColor,
    getHighlightCounts,
    isCreating: createHighlight.isPending,
    isCreatingPageNote: createPageNote.isPending,
    isUpdating: updateHighlight.isPending,
    isDeleting: deleteHighlight.isPending,
  };
}
